/**
 * gaTrack — tiny GA4 helper.
 *
 * All custom events flow through this so we have ONE place to:
 *   • check window.gtag exists (no-ops on dev / blocked browsers)
 *   • enforce the GA4 event-name + property naming conventions
 *   • drop a debug log when REACT_APP_GA_DEBUG=1 is set
 *
 * Convention: snake_case event names + flat snake_case parameter keys.
 * No PII — never pass email, phone, full name, raw IP, etc.
 */
function track(eventName, params = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  try {
    window.gtag("event", eventName, params);
    if (process.env.REACT_APP_GA_DEBUG === "1") {
      // eslint-disable-next-line no-console
      console.debug("[ga4]", eventName, params);
    }
  } catch {
    /* never let analytics break the UI */
  }
}

// ── High-value funnel events ─────────────────────────────────────────
export const gaTrack = {
  /** Fired immediately after OTP verification on /auth registration. */
  signupCompleted({ role, hasRef }) {
    track("signup_completed", {
      role: role || "unknown",
      has_referral_code: !!hasRef,
    });
  },

  /** Fired by the tier-up celebration in-app — once per tier per user. */
  tierUnlocked({ tier }) {
    track("tier_unlocked", { tier: tier || "unknown" });
  },

  /** Fired the moment a user kicks off a CAUL top-up (either rail). */
  caulTopupInitiated({ method, caulAmount, pricingMode }) {
    track("caul_topup_initiated", {
      method: method || "unknown",             // "xaman" | "stripe"
      caul_amount: Number(caulAmount) || 0,
      pricing_mode: pricingMode || "unknown",  // "live" | "package"
    });
  },

  /** Fired after a successful service checkout (Stripe success-status poll). */
  servicePurchased({ category, amount, currency = "USD" }) {
    track("service_purchased", {
      category: category || "unspecified",
      value: Number(amount) || 0,
      currency,
    });
    // Standard GA4 ecommerce event for funnel + monetization reports
    track("purchase", {
      value: Number(amount) || 0,
      currency,
      items: [{ item_category: category || "unspecified" }],
    });
  },

  /** Fired when a tier-share button is clicked anywhere in the app. */
  tierShareClicked({ tier, surface }) {
    track("tier_share_clicked", {
      tier: tier || "unknown",
      surface: surface || "in_app", // "in_app" | "email" (email gets attributed via UTM)
    });
  },
};

export default gaTrack;
